import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { Hash, ShieldCheck, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { CommitteeMemberService } from '../services/committeeMember.service';
import { MediaServices } from '../services/media.service';
import { MEDIA_BASE_URL } from '../lib/env';
import { getFullImageUrl } from '../utils';

const COLUMNS = [
  { header: 'Member Name', accessor: 'member_name' },
  { header: 'Email', accessor: 'member_email' },
  { 
    header: 'Designations', 
    accessor: (row) => Array.isArray(row.designations) && row.designations.length > 0 
      ? row.designations.map(d => d.name).join(', ') 
      : row.member_designation || 'N/A' 
  },
  { header: 'Status', accessor: 'status' }
];



export default function Committee() {
  const navigate = useNavigate();
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Search & Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 5;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await CommitteeMemberService.getAllCommitteeMembers();
      const list = Array.isArray(res) ? res : (res?.data || []);
      const formattedList = list.map(item => ({
        ...item,
        id: item._id || item.id,
      }));
      setDataList(formattedList);
    } catch (error) {
      toast.error('Failed to load committee members');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);



  const filteredData = useMemo(() => {
    let result = dataList;
    if (debouncedQuery) {
      result = result.filter(item =>
        Object.values(item).some(val =>
          String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
        )
      );
    }
    return result;
  }, [dataList, debouncedQuery]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    return [
      { label: "Total Members", value: total, icon: <ShieldCheck size={20} />, color: 'blue' },
      { label: "State Delegations", value: dataList.filter(d => d.zone !== 'National').length, icon: <MapPin size={20} />, color: 'orange' },
      { label: "National Delegations", value: dataList.filter(d => d.zone === 'National').length, icon: <Hash size={20} />, color: 'green' }
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );



  const handleEdit = (row) => {
    const id = row._id || row.id;
    navigate(`/committee/edit/${id}`);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    try {
      const targetId = deleteTarget._id || deleteTarget.id;
      await CommitteeMemberService.deleteCommitteeMember(targetId);
      setDataList(prev => prev.filter(item => item.id !== targetId));
      toast.success('Committee member deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete committee member');
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Committee Members"
        subtitle="Manage executive council designations and representational zones"
        onAdd={() => navigate('/committee/create')}
        addLabel="Add Member"
        kpis={kpis}
      />

      <div className="table-card-container">
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar
              placeholder="Search committee..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable
            columns={COLUMNS}
            data={paginatedData}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={isLoading}
          />
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete Committee Member"
        message="Are you sure you want to delete this executive committee member profile?"
        itemName={deleteTarget ? `${deleteTarget.member_name} (${deleteTarget.member_designation})` : ''}
      />
    </div>
  );
}
